/* eslint-disable max-len */
/* eslint-disable react/destructuring-assignment */
import React from 'react';
import { uniqueId } from 'lodash';
import { useTranslation } from 'react-i18next';
import { FastField, Field, FieldArrayRenderProps, FieldProps, useFormikContext } from 'formik';
import { Select, Popover, callAll } from '@brighterion/br-ui';
import { FormikFieldFreeEntry, FormikInputField } from 'Components/FormikComponents';
import { ParameterTppType } from 'Common/parameterEnums';
import {
    FORBIDDEN_CURRENCY_CHARACTERS,
    FORBIDDEN_NUMERIC_CHARACTERS,
    FORBIDDEN_NUMERIC_INT_CHARACTERS,
    MAX_GLOBAL_FREE_ENTRY_LENGTH,
    getFormattedParameterTypeDisplayName
} from 'Utils';
import { PARAMETERS_PARAM_TYPES, NEW_RULE_CONDITION_VALUE_TYPES, OPERATOR_CONDITIONS } from 'Common/enums';
import { getOperatorOptions } from 'Components/CreateVelocityForm/helper';
import { getDisplayNamesForParameterTypes } from 'Components/AddRulePage/RuleStatement/Condition/ParameterField/ParameterField';
import styles from './Condition.css';

type ConditionsProps = {
    index: number;
    parameters: UseGetParametersResponse;
    conditionsArrayHelper: FieldArrayRenderProps;
};

const FilterConditions = (props: ConditionsProps) => {
    const { t } = useTranslation();
    const { values, isSubmitting, handleBlur, handleChange } = useFormikContext<ParameterVelocityFormSchema>();
    const displayNamesForParameterTypes = getDisplayNamesForParameterTypes(props.parameters.parameterTypeHashmap);

    return (
        <div className={styles.conditionContainer}>
            <div className={styles.conditionColumn}>
                {/** Target field parameter within Condition  */}
                <FastField name={`filters.${props.index}.parameter`}>
                    {({ field, form: { setFieldValue } }: FieldProps) => {
                        return (
                            <Select
                                size='sm'
                                type='search'
                                label='Parameter'
                                searchInputPlaceholder={t('Search')}
                                ellipsis
                                placeholder={`${t('velocityForm.parameter.placeholder')}`}
                                handleClickCallback={(value) => {
                                    setFieldValue(`filters.${props.index}`, {
                                        operator: '',
                                        value: '',
                                        type: props.parameters.fieldNameHashmap[value.toString()].type,
                                        parameter: value.toString()
                                    });
                                }}
                                selectedItems={field.value ? [field.value] : []}
                                disabled={isSubmitting}
                                aria-disabled={isSubmitting}
                                grouped
                            >
                                {displayNamesForParameterTypes.map(({ parameterType, formattedDisplayName }) => {
                                    return (
                                        <optgroup label={formattedDisplayName} key={uniqueId(parameterType)}>
                                            {props.parameters.original
                                                .filter((originalParameter) => {
                                                    /**
                                                     * @note Remove types from Parameter/Attribute list.
                                                     * The following types are NOT supported in Simple Rule.
                                                     */
                                                    const notSupportedParameterListTypes = [
                                                        'string[]',
                                                        'timestamp',
                                                        'pan'
                                                    ];
                                                    return (
                                                        originalParameter.parameterType === parameterType &&
                                                        !notSupportedParameterListTypes.includes(
                                                            originalParameter.type.toLowerCase()
                                                        )
                                                    );
                                                })
                                                .map((parameter) => {
                                                    return (
                                                        <option
                                                            key={uniqueId(String(parameter.id))}
                                                            value={parameter.fieldName}
                                                        >
                                                            <Popover
                                                                showOnHover
                                                                position='bottom-start'
                                                                popperClassName={styles.popOverStyle}
                                                                contentClassName={styles.popOverContentStyle}
                                                                content={
                                                                    <table className={styles.popOverTable}>
                                                                        <tbody>
                                                                            <tr>
                                                                                <td>System Name</td>
                                                                                <td>{parameter?.fieldName ?? '_'}</td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td>Display Name</td>
                                                                                <td>{parameter?.message ?? '_'}</td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td>Type</td>
                                                                                <td>{parameter?.type ?? '_'}</td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td>Parameter Type</td>
                                                                                <td>
                                                                                    {getFormattedParameterTypeDisplayName(
                                                                                        parameter?.parameterType,
                                                                                        'singular'
                                                                                    ) ?? '_'}
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td>Description</td>
                                                                                <td>{parameter?.description ?? '_'}</td>
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>
                                                                }
                                                            >
                                                                <>{parameter.message}</>
                                                            </Popover>
                                                        </option>
                                                    );
                                                })}
                                        </optgroup>
                                    );
                                })}
                            </Select>
                        );
                    }}
                </FastField>
            </div>

            <div className={styles.conditionColumn}>
                {values.filters?.[props.index]?.parameter && (
                    <Field name={`filters.${props.index}.operator`}>
                        {({ field, form: { setFieldValue } }: FieldProps) => {
                            const operationType =
                                props.parameters.fieldNameHashmap[values.filters[props.index].parameter].type;
                            return (
                                <Select
                                    size='sm'
                                    placeholder={`- ${t('velocityForm.operator.placeholder')} -`}
                                    label='Operator'
                                    handleClickCallback={(value) => {
                                        /**
                                         * Remove values from Action
                                         * @note Please, don't get confused by Formik field.value.
                                         */
                                        setFieldValue(`filters.${props.index}.value`, '');
                                        setFieldValue(field.name, value.toString());
                                    }}
                                    selectedItems={field.value ? [field.value] : []}
                                    disabled={isSubmitting}
                                    aria-disabled={isSubmitting}
                                >
                                    {getOperatorOptions(operationType, false).map((operation) => {
                                        return (
                                            <option key={uniqueId(operation)} value={operation}>
                                                {t(`conditions.${operation}`)}
                                            </option>
                                        );
                                    })}
                                </Select>
                            );
                        }}
                    </Field>
                )}
            </div>

            <div className={styles.conditionColumn}>
                {values.filters[props.index].operator && (
                    <Field name={`filters.${props.index}.value`}>
                        {({ field, meta }: FieldProps) => {
                            const { operator } = values.filters[props.index];
                            const selectedParameter =
                                props.parameters.fieldNameHashmap[values.filters[props.index].parameter];
                            switch (operator) {
                                // Text or Numeric Field
                                case OPERATOR_CONDITIONS.EQ:
                                case OPERATOR_CONDITIONS.NEQ:
                                case OPERATOR_CONDITIONS.LT:
                                case OPERATOR_CONDITIONS.LTEQ:
                                case OPERATOR_CONDITIONS.GT:
                                case OPERATOR_CONDITIONS.GTEQ:
                                case OPERATOR_CONDITIONS.STR_EQ:
                                case OPERATOR_CONDITIONS.STR_NEQ:
                                case OPERATOR_CONDITIONS.STR_CONTAINS:
                                case OPERATOR_CONDITIONS.STR_NOT_CONTAINS:
                                    return (
                                        <FormikInputField
                                            name={field.name}
                                            className={styles.formikFieldString}
                                            handleBlur={handleBlur}
                                            label='Value'
                                            type={
                                                selectedParameter.type === PARAMETERS_PARAM_TYPES.STRING
                                                    ? 'text'
                                                    : 'number'
                                            }
                                            handleChange={callAll((e: any) => {
                                                const updatedValue = e.target.value;
                                                props.conditionsArrayHelper.replace(props.index, {
                                                    ...values.filters[props.index],
                                                    value: updatedValue
                                                });
                                                handleChange(e); // propagate changes to the form
                                            })}
                                            handleKeyDown={(event: React.KeyboardEvent) => {
                                                switch (selectedParameter.type) {
                                                    case PARAMETERS_PARAM_TYPES.NUMBER:
                                                        if (selectedParameter.tppType === ParameterTppType.INT) {
                                                            if (FORBIDDEN_NUMERIC_INT_CHARACTERS.includes(event.key)) {
                                                                event.preventDefault();
                                                            }
                                                        } else if (FORBIDDEN_NUMERIC_CHARACTERS.includes(event.key)) {
                                                            event.preventDefault();
                                                        }

                                                        break;
                                                    case PARAMETERS_PARAM_TYPES.CURRENCY:
                                                        if (FORBIDDEN_CURRENCY_CHARACTERS.includes(event.key)) {
                                                            event.preventDefault();
                                                        }
                                                        break;
                                                    default:
                                                        break;
                                                }
                                            }}
                                            disabled={isSubmitting}
                                        />
                                    );

                                // FreeEntry Field
                                case OPERATOR_CONDITIONS.STR_IN:
                                case OPERATOR_CONDITIONS.STR_NOT_IN:
                                    return (
                                        <FormikFieldFreeEntry
                                            name={field.name}
                                            handleChange={() =>
                                                props.conditionsArrayHelper.replace(props.index, {
                                                    ...values.filters[props.index],
                                                    value: field.value.length === 0 ? '' : field.value.join(';'),
                                                    valueType: NEW_RULE_CONDITION_VALUE_TYPES.FREE_ENTRY
                                                })
                                            }
                                            handleBlur={handleBlur}
                                            className={styles.valueInput}
                                            maxLength={MAX_GLOBAL_FREE_ENTRY_LENGTH}
                                            disabled={isSubmitting}
                                            // display error message when users click FreeEntry input field if exists
                                            error={!!(meta.touched && meta.error)}
                                            errorMsg={meta.error?.toString()}
                                        />
                                    );

                                default:
                                    return (
                                        <span className={styles.nomatchingFieldPrompt}>
                                            {t('No Matching Input Field Found')}
                                        </span>
                                    );
                            }
                        }}
                    </Field>
                )}
            </div>
        </div>
    );
};

export default FilterConditions;
